import styled from "@emotion/styled";
import assertNever from "assert-never";
import { LocalDateTime } from "@js-joda/core";
import React from "react";
import { match } from "ts-pattern";
import { HRQuestionId, HRSection } from "../../../messages";
import { STRINGS } from "../../../strings";
import { translate } from "../../../translate";
import { formatDateTime } from "../../../utils/dateUtils";
import { shakeElement } from "../../../utils/shakeElement";
import { CHECK_ICON, LOCK_ICON } from "../../../utils/svgs_new";
import { AppButton } from "../../Generic/AppButton";
import { AppCard, AppCardContent } from "../../Generic/AppCard";
import { AppIcon } from "../../Generic/AppIcon";
import { AppList, AppListItem } from "../../Generic/AppList";
import Flex from "../../Generic/Flex";
import NextButton from "../../NextButton/NextButton";
import OnboardingAccordion from "../OnboardingAccordion";
import {
  SectionCompletionStatus,
  getInterviewDate,
  getSectionCompletionStatus,
  getSectionInfoItems,
  getSectionTitle,
  isQuestionAnswered,
  isQuestionCompleted,
  isRelevantQuestion,
  isSectionLocked,
  mapQuestionGroupsToQuestion,
  mapSectionToQuestionGroup,
} from "../utils/onboardingUtils";
import SectionCardHeader from "./SectionCardHeader";
import SectionInfoItem from "./SectionInfoItem";
import { flattenSectionGroups } from "../../OnboardingSection/hooks/useStage";

interface Props {
  section: HRSection;
  language: string;
  isCriminalBackgroundCompliant: boolean;
  isInterviewCompliant: boolean | null;
  onClickLocked: () => void;
  onClick: () => void;
  onClickQuestion: (questionId: HRQuestionId) => void;
}

const SectionCard = (props: Props) => {
  const section = props.section;
  const sectionStatus = getSectionCompletionStatus(
    section,
    props.isCriminalBackgroundCompliant,
    props.isInterviewCompliant
  );
  const isLocked = isSectionLocked(
    section,
    props.isCriminalBackgroundCompliant,
    props.isInterviewCompliant
  );
  const isCompleted = sectionStatus === "Completed";
  const questions = mapQuestionGroupsToQuestion(mapSectionToQuestionGroup(section)).filter(
    isRelevantQuestion
  );
  const answeredQuestions = questions.filter(isQuestionAnswered);
  const completedQuestions = answeredQuestions.filter(isQuestionCompleted);
  const { questionGroupIdComplianceMap } = flattenSectionGroups(props.section);
  const sectionItems = getSectionInfoItems({
    section,
    questions,
    answeredQuestions,
    completedQuestions,
    isCriminalBackgroundCompliant: props.isCriminalBackgroundCompliant,
    isInterviewCompliant: props.isInterviewCompliant,
    questionGroupIdComplianceMap,
  });
  const hasSectionItems = sectionItems.length > 0;
  const completePercentage = Math.round((completedQuestions.length / questions.length) * 100);
  const headerRef = React.useRef<HTMLDivElement>(null);
  const interviewDate = getInterviewDate(section);

  const iconColor =
    interviewDate !== null && interviewDate.isAfter(LocalDateTime.now()) && !isCompleted
      ? "var(--primary-color)"
      : match(sectionStatus)
          .with("Completed", () => "var(--tw-green-600)")
          .with("Incomplete", () => "var(--tw-slate-400)")
          .with("Not Started", () => "var(--tw-slate-400)")
          .with("Pending Review", () => "var(--primary-color)")
          .with("Rejected", () => "var(--tw-red-500)")
          .exhaustive();

  const handleClick = () => {
    if (isCompleted) {
      return props.onClick();
    }

    if (isLocked) {
      shakeElement(headerRef, { duration: 600 });
      return props.onClickLocked();
    }

    if (!hasSectionItems) {
      return props.onClick();
    }
  };

  const isCollapsible = !isLocked && !isCompleted && hasSectionItems;

  const overrideSubtitle =
    interviewDate === null
      ? undefined
      : `${translate(STRINGS.SCHEDULED_FOR)} ${formatDateTime(interviewDate)}`;

  return (
    <SectionCard.Styled>
      <OnboardingAccordion.Trigger asChild>
        <SectionCardHeader
          ref={headerRef}
          icon={<SectionIcon status={sectionStatus} color={iconColor} />}
          title={
            <Flex direction="column" gap={4}>
              <div>{getSectionTitle(section, props.language)}</div>
              <SectionCard.SectionStatus color={iconColor}>
                {overrideSubtitle === undefined
                  ? getSectionStatusTranslated(sectionStatus)
                  : overrideSubtitle}
              </SectionCard.SectionStatus>
            </Flex>
          }
          trailing={
            <SectionCardHeaderTrailing
              isLocked={isLocked}
              status={sectionStatus}
              isCollapsible={isCollapsible}
            />
          }
          isLocked={isLocked}
          completePercentage={completePercentage}
          onClick={handleClick}
        />
      </OnboardingAccordion.Trigger>
      {!isCollapsible ? null : (
        <OnboardingAccordion.Content>
          <AppCardContent>
            <SectionCard.AppList bordered>
              {sectionItems.map((item, i) => (
                <SectionInfoItem
                  key={i}
                  item={item}
                  language={props.language}
                  onClick={props.onClick}
                  onClickQuestion={props.onClickQuestion}
                />
              ))}
            </SectionCard.AppList>
          </AppCardContent>
        </OnboardingAccordion.Content>
      )}
    </SectionCard.Styled>
  );
};

function SectionCardHeaderTrailing(props: {
  isLocked: boolean;
  status: SectionCompletionStatus;
  isCollapsible: boolean;
}) {
  const { isLocked, status } = props;

  if (isLocked) {
    return <AppIcon icon={LOCK_ICON} size={16} fill="var(--tw-slate-600)" />;
  }

  if (status === "Completed") {
    return null;
  }

  if (status === "Pending Review") {
    return <OnboardingAccordion.Chevron size={16} isCollapsible={props.isCollapsible} />;
  }

  const color = (() => {
    switch (status) {
      case "Rejected":
        return "danger";

      case "Incomplete":
      case "Not Started":
        return "primary";

      default:
        assertNever(status);
    }
  })();

  const text = (() => {
    switch (status) {
      case "Rejected":
        return translate(STRINGS.FIX);

      case "Incomplete":
        return translate(STRINGS.FILL);

      case "Not Started":
        return translate(STRINGS.FILL);

      default:
        assertNever(status);
    }
  })();

  return (
    <AppButton size="small" variant="soft" color={color}>
      <Flex direction="row" alignItems="center" xGap={4}>
        <span>{text}</span>
        <OnboardingAccordion.Chevron size={12} isCollapsible={props.isCollapsible} />
      </Flex>
    </AppButton>
  );
}

function getSectionStatusTranslated(sectionStatus: ReturnType<typeof getSectionCompletionStatus>) {
  return match(sectionStatus)
    .with("Incomplete", () => translate(STRINGS.INCOMPLETE))
    .with("Completed", () => translate(STRINGS.COMPLETED))
    .with("Not Started", () => translate(STRINGS.NOT_STARTED))
    .with("Pending Review", () => translate(STRINGS.PENDING_APPROVAL))
    .with("Rejected", () => translate(STRINGS.REJECTED))
    .exhaustive();
}

function SectionIcon(props: { status: SectionCompletionStatus; color: string }) {
  const icon = match(props.status)
    .with("Incomplete", () => <Circle color={props.color} />)
    .with("Completed", () => CHECK_ICON)
    .with("Not Started", () => <Circle color={props.color} />)
    .with("Pending Review", () => <Circle color={props.color} />)
    .with("Rejected", () => <Circle color={props.color} />)
    .exhaustive();

  return (
    <SectionCard.IconWrapper color={props.color}>
      <AppIcon size={10} fill={props.color} stroke={props.color} icon={icon} />
    </SectionCard.IconWrapper>
  );
}

const Circle = styled.div<{ color: string }>`
  width: 6px !important;
  height: 6px !important;
  border-radius: 50%;
  background-color: ${(props) => props.color};
`;

SectionCard.Title = styled.span`
  color: #000000;
  font-size: 16px;
  font-weight: 600;
`;

SectionCard.SectionStatus = styled.span<{ color: string }>`
  color: ${(props) => props.color};
  font-size: 14px;
  font-weight: 600;
`;

SectionCard.SectionInfo = styled.div`
  margin-top: 12px;
  font-size: 14px;
  color: var(--gray-500);
`;

SectionCard.IconWrapper = styled.div<{ color: string }>`
  --size: 18px;
  border: 2px solid var(--border-color, var(--primary-color));
  background: #ffffff;
  width: var(--size);
  height: var(--size);
  border-radius: var(--size);
  color: ${(props) => props.color};
  display: flex;
  align-items: center;
  justify-content: center;

  --border-color: ${(props) => props.color};
`;

SectionCard.AppList = styled(AppList)`
  width: 100%;
`;

SectionCard.AppListItem = styled(AppListItem)`
  justify-content: space-between;
`;

SectionCard.Styled = styled(AppCard)`
  .AppCardContent {
    display: flex;
    gap: 12px;
    flex-direction: column;
    padding-top: 0;
    padding-bottom: 0;
  }

  ${SectionCard.IconWrapper}, ${NextButton.Styled} {
    flex-shrink: 0;
  }

  h3 {
    all: unset;
  }
`;

export default SectionCard;
